/**
 * CRM API Layer.
 *
 * React Query hooks for CRM activities, customer equipment, quotes,
 * referrals, email templates, sequences, pipeline analytics, and
 * customer scoring.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import type { PaginatedResponse } from "@/types/common";

// ---------------------------------------------------------------------------
// CRM Activities
// ---------------------------------------------------------------------------

export interface CRMActivity {
  id: string;
  organization_id: string;
  activity_type: string;
  subject: string;
  description: string | null;
  deal_id: string | null;
  customer_id: string | null;
  contact_id: string | null;
  sales_order_id: string | null;
  due_date: string | null;
  completed_at: string | null;
  is_completed: boolean;
  direction: string | null;
  duration_minutes: number | null;
  outcome: string | null;
  assigned_to: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export function useCRMActivities(
  params: {
    customer_id?: string;
    deal_id?: string;
    is_completed?: boolean;
    page?: number;
  } = {},
) {
  return useQuery<PaginatedResponse<CRMActivity>>({
    queryKey: ["crm-activities", params],
    queryFn: async () => {
      const sp = new URLSearchParams();
      if (params.customer_id) sp.set("customer_id", params.customer_id);
      if (params.deal_id) sp.set("deal_id", params.deal_id);
      if (params.is_completed !== undefined)
        sp.set("is_completed", String(params.is_completed));
      sp.set("page", String(params.page || 1));
      return (await apiClient.get(`/api/v1/crm/activities?${sp}`)).data;
    },
  });
}

export function useCreateActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) =>
      (await apiClient.post("/api/v1/crm/activities", data)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["crm-activities"] }),
  });
}

export function useUpdateActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Record<string, unknown>;
    }) => (await apiClient.put(`/api/v1/crm/activities/${id}`, data)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["crm-activities"] }),
  });
}

export function useTaskQueue(daysAhead = 7) {
  return useQuery<CRMActivity[]>({
    queryKey: ["task-queue", daysAhead],
    queryFn: async () =>
      (
        await apiClient.get(
          `/api/v1/crm/activities/tasks?days_ahead=${daysAhead}`,
        )
      ).data,
  });
}

// ---------------------------------------------------------------------------
// Customer Equipment
// ---------------------------------------------------------------------------

export interface CustomerEquipmentItem {
  id: string;
  customer_id: string;
  asset_id: string | null;
  make: string | null;
  model: string | null;
  year: number | null;
  serial_number: string | null;
  category: string | null;
  purchase_date: string | null;
  purchase_source: string;
  hours: number | null;
  mileage: number | null;
  status: string;
  notes: string | null;
  created_at: string;
}

export function useCustomerEquipment(customerId: string) {
  return useQuery<CustomerEquipmentItem[]>({
    queryKey: ["customer-equipment", customerId],
    queryFn: async () =>
      (
        await apiClient.get(
          `/api/v1/customer-equipment?customer_id=${customerId}`,
        )
      ).data,
    enabled: !!customerId,
  });
}

export function useCreateEquipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) =>
      (await apiClient.post("/api/v1/customer-equipment", data)).data,
    onSuccess: (_, vars) =>
      qc.invalidateQueries({
        queryKey: [
          "customer-equipment",
          (vars as Record<string, unknown>).customer_id,
        ],
      }),
  });
}

// ---------------------------------------------------------------------------
// Quotes
// ---------------------------------------------------------------------------

export interface QuoteItem {
  id: string;
  quote_number: string;
  customer_id: string;
  customer_name: string | null;
  deal_id: string | null;
  status: string;
  version: number;
  subtotal: number;
  tax: number;
  shipping: number;
  discount: number;
  total: number;
  trade_in_credit: number;
  valid_until: string | null;
  payment_terms: string | null;
  delivery_terms: string | null;
  share_token: string | null;
  converted_to_so_id: string | null;
  financing_options: Array<Record<string, unknown>> | null;
  notes: string | null;
  internal_notes: string | null;
  line_items: Array<{
    id: string;
    description: string;
    asset_id?: string;
    quantity: number;
    unit_price: number;
    line_total: number;
  }>;
  tax_lines: import("@/types/sales-order").OrderTaxLine[];
  created_at: string;
}

export function useQuotes(
  params: { customer_id?: string; deal_id?: string } = {},
) {
  return useQuery<PaginatedResponse<QuoteItem>>({
    queryKey: ["quotes", params],
    queryFn: async () => {
      const sp = new URLSearchParams();
      if (params.customer_id) sp.set("customer_id", params.customer_id);
      if (params.deal_id) sp.set("deal_id", params.deal_id);
      return (await apiClient.get(`/api/v1/quotes?${sp}`)).data;
    },
  });
}

export function useQuote(quoteId: string | undefined) {
  return useQuery<QuoteItem>({
    queryKey: ["quotes", quoteId],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/quotes/${quoteId}`)).data,
    enabled: !!quoteId,
  });
}

export function useCreateQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) =>
      (await apiClient.post("/api/v1/quotes", data)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quotes"] }),
  });
}

// ---------------------------------------------------------------------------
// Referrals
// ---------------------------------------------------------------------------

export interface ReferralItem {
  id: string;
  referring_customer_id: string;
  referred_customer_id: string | null;
  referred_name: string;
  referred_contact: string | null;
  deal_id: string | null;
  status: string;
  referral_date: string;
  converted_date: string | null;
  referral_value: number | null;
  notes: string | null;
  created_at: string;
}

export function useReferrals(customerId?: string) {
  return useQuery<ReferralItem[]>({
    queryKey: ["referrals", customerId],
    queryFn: async () => {
      const sp = customerId ? `?referring_customer_id=${customerId}` : "";
      return (await apiClient.get(`/api/v1/referrals${sp}`)).data;
    },
  });
}

export function useCreateReferral() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) =>
      (await apiClient.post("/api/v1/referrals", data)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["referrals"] }),
  });
}

export function useReferralLeaderboard() {
  return useQuery<
    Array<{
      customer_id: string;
      total_referrals: number;
      converted: number;
      total_value: number;
    }>
  >({
    queryKey: ["referral-leaderboard"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/referrals/leaderboard")).data,
  });
}

// ---------------------------------------------------------------------------
// Pipeline Analytics
// ---------------------------------------------------------------------------

export interface PipelineStageSummary {
  stage: string;
  count: number;
  total_value: number;
  weighted_value: number;
  avg_days_in_stage: number;
  stale_count: number;
}

export interface PipelineSummary {
  pipeline: string;
  stages: PipelineStageSummary[];
  total_deals: number;
  total_value: number;
  total_weighted: number;
  win_rate: number;
  avg_deal_cycle_days: number;
}

export function usePipelineSummary(pipeline = "sales") {
  return useQuery<PipelineSummary>({
    queryKey: ["pipeline-summary", pipeline],
    queryFn: async () =>
      (
        await apiClient.get(
          `/api/v1/crm/analytics/pipeline-summary?pipeline=${pipeline}`,
        )
      ).data,
  });
}

export interface ForecastPeriod {
  period: string;
  commit: number;
  best_case: number;
  pipeline_value: number;
  weighted_total: number;
  closed_won: number;
}

export function useForecast(pipeline = "sales") {
  return useQuery<ForecastPeriod[]>({
    queryKey: ["forecast", pipeline],
    queryFn: async () =>
      (
        await apiClient.get(
          `/api/v1/crm/analytics/forecast?pipeline=${pipeline}`,
        )
      ).data,
  });
}

export interface WinLossData {
  total_won: number;
  total_lost: number;
  win_rate: number;
  avg_won_value: number;
  avg_lost_value: number;
  top_win_reasons: Array<{ reason: string; count: number }>;
  top_loss_reasons: Array<{ reason: string; count: number }>;
  by_source: Array<{
    source: string;
    won: number;
    lost: number;
    total_value: number;
  }>;
  by_rep: Array<{
    rep_id: string;
    won: number;
    lost: number;
    total_value: number;
  }>;
}

export function useWinLossAnalysis(pipeline = "sales", daysBack = 90) {
  return useQuery<WinLossData>({
    queryKey: ["win-loss", pipeline, daysBack],
    queryFn: async () =>
      (
        await apiClient.get(
          `/api/v1/crm/analytics/win-loss?pipeline=${pipeline}&days_back=${daysBack}`,
        )
      ).data,
  });
}

export interface CustomerScore {
  customer_id: string;
  rfm_recency_score: number;
  rfm_frequency_score: number;
  rfm_monetary_score: number;
  rfm_composite_score: number;
  health_score: number;
  segment: string | null;
  lifetime_value: number;
  total_orders: number;
  first_purchase_date: string | null;
  last_purchase_date: string | null;
  avg_purchase_cycle_days: number | null;
  next_predicted_purchase: string | null;
}

export function useCustomerScore(customerId: string) {
  return useQuery<CustomerScore>({
    queryKey: ["customer-score", customerId],
    queryFn: async () =>
      (
        await apiClient.get(
          `/api/v1/crm/analytics/customer-scores/${customerId}`,
        )
      ).data,
    enabled: !!customerId,
  });
}

export function useScoreCustomers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (customerId?: string) => {
      const sp = customerId ? `?customer_id=${customerId}` : "";
      return (
        await apiClient.post(`/api/v1/crm/analytics/score-customers${sp}`)
      ).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["customer-score"] });
      qc.invalidateQueries({ queryKey: ["customers"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Email Templates & Sequences
// ---------------------------------------------------------------------------

export interface EmailTemplateItem {
  id: string;
  name: string;
  category: string;
  subject: string;
  body: string;
  is_active: boolean;
  created_at: string;
}

export function useEmailTemplates(category?: string) {
  return useQuery<EmailTemplateItem[]>({
    queryKey: ["email-templates", category],
    queryFn: async () => {
      const sp = category ? `?category=${category}` : "";
      return (await apiClient.get(`/api/v1/email/templates${sp}`)).data;
    },
  });
}

export function useCreateEmailTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) =>
      (await apiClient.post("/api/v1/email/templates", data)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["email-templates"] }),
  });
}

export interface SequenceItem {
  id: string;
  name: string;
  description: string | null;
  steps: Array<Record<string, unknown>>;
  is_active: boolean;
  created_at: string;
}

export function useSequences() {
  return useQuery<SequenceItem[]>({
    queryKey: ["sequences"],
    queryFn: async () => (await apiClient.get("/api/v1/email/sequences")).data,
  });
}

export function useCreateSequence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) =>
      (await apiClient.post("/api/v1/email/sequences", data)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sequences"] }),
  });
}
